import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../../i18n'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../contexts/AuthContext'
import { useUserPreferences } from '../../contexts/UserPreferencesContext'
import { usePopup } from '../../hooks/usePopup'
import { userApi } from '../../api'
import { getDefaultShortcuts, getItemsForRole } from '../../config/quickAccessItems'
import { Button } from '../ui'

const MAX_SHORTCUTS = 3

export default function QuickAccessSettings() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const { user } = useAuth()
  const { preferences, refreshPreferences } = useUserPreferences()
  const { showSuccess, showError } = usePopup()

  const availableItems = getItemsForRole(user?.hasTrainees ?? false, user?.hasCoaches ?? false)
  const availableKeys = new Set(availableItems.map(item => item.key))

  const initialKeys = (
    preferences?.pinnedShortcuts?.length
      ? preferences.pinnedShortcuts
      : getDefaultShortcuts(user?.hasTrainees ?? false, user?.hasCoaches ?? false)
  ).filter(key => availableKeys.has(key))

  const [selected, setSelected] = useState<string[]>(initialKeys)
  const [saving, setSaving] = useState(false)

  function toggle(key: string) {
    setSelected(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key)
      if (prev.length >= MAX_SHORTCUTS) return [...prev.slice(1), key]
      return [...prev, key]
    })
  }

  async function handleSave() {
    try {
      setSaving(true)
      await userApi.updateUserPreferences({ pinnedShortcuts: selected })
      await refreshPreferences()
      showSuccess(t.quickAccess.saveSuccess)
      navigate(-1)
    } catch {
      showError(t.quickAccess.saveError)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-5">
      <header className="mb-5">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {t.quickAccess.title}
        </h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {t.quickAccess.selectHint}
        </p>
      </header>

      <div className="flex flex-col gap-2 mb-6">
        {availableItems.map(item => {
          const isSelected = selected.includes(item.key)
          const order = selected.indexOf(item.key)
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => toggle(item.key)}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl text-left active:opacity-75 transition-opacity"
              style={{
                backgroundColor: isSelected ? `${theme.buttonColor}15` : theme.cardBg,
                border: `1px solid ${isSelected ? theme.buttonColor : theme.cardBorder}`,
              }}
            >
              <span
                className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
                style={{ backgroundColor: isSelected ? `${theme.buttonColor}20` : `${theme.text.muted}12` }}
              >
                {item.emoji}
              </span>
              <span className="flex-1 text-sm font-medium" style={{ color: theme.text.primary }}>
                {t.quickAccess.items[item.labelKey]}
              </span>
              {isSelected && (
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: theme.buttonColor, color: '#fff' }}
                >
                  {order + 1}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <Button onClick={handleSave} disabled={saving || selected.length === 0} fullWidth>
        {t.quickAccess.saveButton}
      </Button>
    </div>
  )
}
